// 設定 › 本地 VMS › 確認（SPEC #vms-web-offline 步驟 1 與 3；畫面 offline-vms/s10、offline-vms-r4/s2）。
// /settings/local-vms/approve?req=<登記請求 id>：每 2 秒輪詢這筆請求——
//   waiting_key：等本地分頁掛上這台電腦（20 秒還沒掛上才顯示「新分頁沒有打開本地畫面？」）
//   key_attached：自己切到確認畫面（主網址核對碼、備援「等待中 / 已加入（只能看）」即時狀態、電腦名稱、6 位數 PIN）
//   → approve（403 REAUTH_REQUIRED 由 StepUpHost 處理後重送）
//   approved：完成；expired：過期，回清單重來。
import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useQueries, useQuery } from '@tanstack/react-query';
import { isKinioError } from '@kinio/web-sdk';
import { useScope } from '../../lib/scope';
import { guessLabel, localVmsApi, NO_KEYS_HINT_MS, POLL_MS, validPin, type EnrollRequestState } from '../../lib/localVms';
import { lvErr } from './LocalVmsSection';
import { useEmailVerifyPrompt } from '../../components/EmailVerifyPrompt';

interface NavState {
  api?: string;
  station_name?: string;
  device_ref?: string;
  enroll_url?: string;
  blocked?: boolean;
}

/** 找這筆請求在哪個 plane：從清單頁來的有帶；直接開網址（重新整理）就逐一試。 */
async function locate(apis: string[], id: string): Promise<{ api: string; req: EnrollRequestState }> {
  let last: unknown = null;
  for (const api of apis) {
    try {
      return { api, req: await localVmsApi.getRequest(api, id) };
    } catch (e) {
      last = e;
      if (!(isKinioError(e) && (e.status === 404 || e.status === 403))) throw e;
    }
  }
  throw last ?? new Error('not found');
}

export function LocalVmsApprove() {
  const { t } = useTranslation();
  const [sp] = useSearchParams();
  const id = sp.get('req') ?? '';
  const nav = (useLocation().state ?? {}) as NavState;
  const navigate = useNavigate();
  const { planes } = useScope();
  const apis = nav.api ? [nav.api] : planes.map((p) => p.api);
  const [done, setDone] = useState(false);
  const [late, setLate] = useState(false);

  const q = useQuery({
    queryKey: ['local-vms-request', id, apis.join(',')],
    queryFn: () => locate(apis, id),
    enabled: !!id && apis.length > 0 && !done,
    retry: false,
    refetchInterval: (query) => {
      const s = query.state.data?.req.status;
      return s === 'approved' || s === 'expired' || query.state.error ? false : POLL_MS;
    },
  });
  useEffect(() => {
    const h = setTimeout(() => setLate(true), NO_KEYS_HINT_MS);
    return () => clearTimeout(h);
  }, [id]);

  const req = q.data?.req;
  const api = q.data?.api;
  // 輪詢回應沒有 Station 名稱：從清單頁帶過來；重新整理後用 device_ref 對可登記的 Station 清單
  const sq = useQueries({ queries: nav.station_name ? [] : apis.map((a) => ({ queryKey: ['local-vms-stations', a], queryFn: () => localVmsApi.stations(a), retry: false })) });
  const station = nav.station_name || sq.flatMap((x) => x.data ?? []).find((s) => s.device_ref === req?.device_ref)?.display_name || req?.device_ref || '';
  const back = () => navigate('/settings/local-vms');

  let body;
  if (!id || q.isError) {
    body = (
      <>
        <h2 className="section-title">{t('localvms.req_missing_title')}</h2>
        <p className="muted">{q.isError ? lvErr(t, q.error) : t('localvms.req_missing')}</p>
        <div className="row-end" style={{ justifyContent: 'flex-start' }}>
          <button className="btn" onClick={back}>
            {t('localvms.back')}
          </button>
        </div>
      </>
    );
  } else if (done || req?.status === 'approved') {
    body = (
      <div data-testid="local-vms-done">
        <h2 className="section-title">{t('localvms.done_title')}</h2>
        <p>{t('localvms.done_body', { station })}</p>
        <div className="row-end" style={{ justifyContent: 'flex-start' }}>
          <Link className="btn" to="/settings/local-vms">
            {t('localvms.back')}
          </Link>
        </div>
      </div>
    );
  } else if (req?.status === 'expired') {
    body = (
      <div data-testid="local-vms-expired">
        <h2 className="section-title">{t('localvms.expired_title')}</h2>
        <p className="muted">{t('localvms.expired_body')}</p>
        <div className="row-end" style={{ justifyContent: 'flex-start' }}>
          <button className="btn" onClick={back}>
            {t('localvms.back')}
          </button>
        </div>
      </div>
    );
  } else if (req?.status === 'key_attached' && api) {
    body = <ConfirmForm api={api} id={id} req={req} station={station} onDone={() => setDone(true)} onCancel={back} />;
  } else {
    body = (
      <div data-testid="local-vms-waiting">
        {late ? (
          <>
            <h2 className="section-title">{t('localvms.no_tab_title')}</h2>
            <p className="muted">{t('localvms.no_tab_body', { station })}</p>
            <p className="muted">{t('localvms.no_tab_continue')}</p>
          </>
        ) : (
          <>
            <h2 className="section-title">{t('localvms.waiting_title')}</h2>
            <p className="muted">{t('localvms.waiting_body', { station })}</p>
          </>
        )}
        {nav.blocked && nav.enroll_url && <p className="small">{t('localvms.popup_blocked')}</p>}
        <div className="row-end" style={{ justifyContent: 'flex-start' }}>
          {nav.enroll_url && (
            <a className="btn" href={nav.enroll_url} target="_blank" rel="noopener noreferrer">
              {t('localvms.reopen')}
            </a>
          )}
          <button className="btn" onClick={back}>
            {t('common.cancel')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <section className="card" style={{ maxWidth: 680 }} data-testid="local-vms-approve">
      {body}
    </section>
  );
}

function ConfirmForm({ api, id, req, station, onDone, onCancel }: { api: string; id: string; req: EnrollRequestState; station: string; onDone(): void; onCancel(): void }) {
  const { t } = useTranslation();
  const [label, setLabel] = useState(() => guessLabel(navigator.userAgent));
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const verify = useEmailVerifyPrompt(); // 403 EMAIL_NOT_VERIFIED（platform#176）
  const fb = req.fallback;
  const ok = label.trim().length > 0 && validPin(pin);
  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!ok) return;
    setBusy(true);
    setErr(null);
    try {
      await localVmsApi.approve(api, id, { label: label.trim(), pin });
      onDone();
    } catch (x) {
      if (!verify.ask(x)) setErr(lvErr(t, x));
    } finally {
      setBusy(false);
    }
  }
  return (
    <form className="form" onSubmit={submit} data-testid="local-vms-confirm">
      {verify.prompt}
      <h2 className="section-title">{t('localvms.confirm_title', { station })}</h2>
      <label className="field">
        <span>{t('localvms.computer_name')}</span>
        <input className="input" required maxLength={64} value={label} onChange={(x) => setLabel(x.target.value)} placeholder={t('localvms.computer_name_ph')} />
      </label>
      <p className="muted small">{t('localvms.confirm_body')}</p>
      <div>
        <strong>{t('localvms.code_main')}</strong>{' '}
        <span className="mono" data-testid="local-vms-code" style={{ fontSize: 24, letterSpacing: '0.2em', fontWeight: 700 }}>
          {req.main?.check_code}
        </span>{' '}
        <span className="muted small">{t('localvms.code_hint')}</span>
      </div>
      <div data-testid="local-vms-fallback" data-state={fb}>
        <strong>{t('localvms.fallback_label')}</strong>{' '}
        <span className={'badge' + (fb === 'joined' ? ' badge-ok' : '')}>{fb === 'joined' ? t('localvms.fallback_attached') : t('localvms.fallback_waiting')}</span>{' '}
        <span className="muted small">{t('localvms.fallback_hint')}</span>
      </div>
      <label className="field">
        <span>{t('localvms.pin_label')}</span>
        <input
          className="input mono"
          type="password"
          inputMode="numeric"
          autoComplete="new-password"
          maxLength={6}
          value={pin}
          onChange={(x) => setPin(x.target.value.replace(/\D/g, '').slice(0, 6))}
          placeholder="••••••"
          style={{ maxWidth: 160 }}
        />
      </label>
      <p className="muted small">{t('localvms.stepup_note')}</p>
      {err && (
        <div className="error" role="alert">
          {err}
        </div>
      )}
      <div className="row-end" style={{ justifyContent: 'flex-start' }}>
        <button type="button" className="btn" onClick={onCancel}>
          {t('common.cancel')}
        </button>
        <button type="submit" className="btn btn-primary" disabled={busy || !ok}>
          {t('localvms.approve')}
        </button>
      </div>
    </form>
  );
}
